/**
 * Elite Vending Machine - CSS Logo Component
 *
 * A stylized logo for Elite tenant using pure CSS/SVG
 */

interface EliteLogoProps {
  size?: 'sm' | 'md' | 'lg';
  showText?: boolean;
  className?: string;
}

export function EliteLogo({ size = 'md', showText = true, className = '' }: EliteLogoProps) {
  const sizeClasses = {
    sm: { icon: 'w-8 h-8', text: 'text-sm', container: 'gap-2' },
    md: { icon: 'w-10 h-10', text: 'text-base', container: 'gap-2' },
    lg: { icon: 'w-14 h-14', text: 'text-xl', container: 'gap-3' },
  };

  const s = sizeClasses[size];

  return (
    <div className={`flex items-center ${s.container} ${className}`}>
      {/* Icon - Stylized "E" with vending machine elements */}
      <div
        className={`${s.icon} relative flex items-center justify-center rounded-lg`}
        style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
          boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3), 0 2px 4px -1px rgba(59, 130, 246, 0.2)',
        }}
      >
        {/* Vending machine icon with "E" */}
        <svg
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full p-1.5"
        >
          {/* Machine body */}
          <rect x="6" y="4" width="28" height="32" rx="3" fill="white" fillOpacity="0.95" />

          {/* Display window */}
          <rect x="9" y="7" width="22" height="14" rx="2" fill="#1e40af" fillOpacity="0.15" />

          {/* "E" letter stylized as product rows */}
          <rect x="12" y="9" width="16" height="2.5" rx="0.5" fill="#1e40af" />
          <rect x="12" y="13" width="12" height="2.5" rx="0.5" fill="#3b82f6" />
          <rect x="12" y="17" width="16" height="2.5" rx="0.5" fill="#1e40af" />

          {/* Keypad area */}
          <rect x="9" y="24" width="14" height="9" rx="1.5" fill="#f1f5f9" />
          <circle cx="12" cy="27" r="1.5" fill="#94a3b8" />
          <circle cx="16" cy="27" r="1.5" fill="#94a3b8" />
          <circle cx="20" cy="27" r="1.5" fill="#94a3b8" />
          <circle cx="12" cy="30" r="1.5" fill="#94a3b8" />
          <circle cx="16" cy="30" r="1.5" fill="#94a3b8" />
          <circle cx="20" cy="30" r="1.5" fill="#94a3b8" />

          {/* Dispensing slot */}
          <rect x="25" y="24" width="6" height="9" rx="1" fill="#0f172a" fillOpacity="0.8" />
          <rect x="26" y="29" width="4" height="3" rx="0.5" fill="#1e40af" fillOpacity="0.3" />
        </svg>

        {/* Shine effect */}
        <div
          className="absolute inset-0 rounded-lg opacity-30"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%)',
          }}
        />
      </div>

      {/* Text */}
      {showText && (
        <div className="flex flex-col leading-tight">
          <span
            className={`font-bold tracking-tight ${s.text}`}
            style={{ color: '#1e40af' }}
          >
            Elite
          </span>
          <span
            className="text-xs text-slate-500 font-medium -mt-0.5"
          >
            Vending
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Elite Logo as a simple icon (no text)
 */
export function EliteLogoIcon({ size = 'md', className = '' }: Omit<EliteLogoProps, 'showText'>) {
  return <EliteLogo size={size} showText={false} className={className} />;
}

export default EliteLogo;
